import { useEffect, useId, useRef, useState } from "react";
import { styled } from "styled-components";

import {
  EllipsisHorizontalIcon,
  PencilIcon,
  PlusIcon,
  TickIcon,
  UserGroupIcon,
} from "@vericus/cadmus-icons";
import {
  Alert,
  Avatar,
  Button,
  colors,
  Divider,
  DropdownMenu,
  IconButton,
  LinkButton,
  Pill,
  Popover,
  ScrollArea,
  Switch,
  Text,
  Tooltip,
} from "@vericus/cadmus-ui";

import { show } from "@ebay/nice-modal-react";
import copy from "copy-to-clipboard";
import toast from "react-hot-toast";

import { FinaliseGroupModal } from "@/features/group/components/finalise-group-modal";
import { useEmailConsent, useFinaliseGroup } from "@/features/group/hooks";
import { getActiveMembers, getWithdrawnMembers } from "@/features/group/utils";
import { GroupChangeRequestModal } from "@/features/home/components/group-change-request-modal";
import {
  AssessmentFragment,
  GroupChangeRequestStatus,
  GroupFragment,
  MessageFragment,
  useCurrentUserQuery,
  UserFragment,
} from "@/generated/graphql";
import { useRootNavigate } from "@/routes";
import { formatDateFull, formatDateShort } from "@/utils/datetime";

import { useRowFit } from "../hooks/use-row-fit";
import * as styles from "./group-card.css";
import { MEMBER_LIST_GAP } from "./group-card.css";
import * as homeStyles from "./home-page.css";
import { RailCardLayoutProps } from "./rail-card-layout";

interface GroupCardProps extends RailCardLayoutProps {
  assessment: AssessmentFragment;
  finalised: boolean;
  userGroup: GroupFragment | null;
  changeRequested?: MessageFragment | null;
  workGroupId?: string;
}

export function GroupCard(props: GroupCardProps) {
  const { assessment, finalised, layout = "wide", title } = props;
  const emailConsent = useEmailConsent(assessment.id);
  // `meId` only hides the stacked member menu on the viewer's own row, so skip
  // the query where it is never read: the wide layout has no menu, and a
  // groupless card renders no member list.
  const { data: currentUserData } = useCurrentUserQuery({
    skip: layout !== "stacked" || !props.userGroup,
  });
  const meId = currentUserData?.me?.id;

  const settings = assessment.settings;
  const selfEnrolled = settings?.groupSelfEnrollmentEnabled;
  const deadline = settings?.groupSelfEnrollmentDeadline;

  const isDeadlinePassed =
    deadline != null ? new Date(deadline) < new Date() : false;
  const isFinalised = isDeadlinePassed || finalised;

  // Identical content in both layouts. Only where it sits relative to the
  // member list, below in `wide`, above in `stacked`, differs below.
  const content = (
    <div>
      <Content {...props} />
      {selfEnrolled === true && deadline != null && (
        <>
          <ContentDivider />
          <div className={styles.progress}>
            {isFinalised && !props.userGroup ? (
              <Pill color={colors.failureBg}>Self Enrolment Closed</Pill>
            ) : !isFinalised ? (
              <Pill color={colors.successBg}>Group Formation In Progress</Pill>
            ) : null}
            <Text kind="bodySm">
              <strong> Due:</strong>{" "}
              {layout === "stacked"
                ? formatDateShort(new Date(deadline))
                : formatDateFull(new Date(deadline))}
            </Text>
          </div>
        </>
      )}
    </div>
  );

  const actions = (
    <div className={styles.groupCardActions}>
      {settings?.openDate != null && (
        <ButtonComponent
          {...props}
          selfEnrolled={selfEnrolled ?? false}
          isFinalised={isFinalised}
        />
      )}
      {/* Outside the `openDate` gate and the self-enrolment ladder in
          `ButtonComponent`: an existing request's outcome is the student's only
          record of it, so it must survive both. */}
      <ChangeRequestSection {...props} />
      {emailConsent.enabled && props.userGroup && (
        <EmailConsentToggle emailConsent={emailConsent} layout={layout} />
      )}
    </div>
  );

  const members = props.userGroup ? (
    <GroupMembers
      userGroup={props.userGroup}
      layout={layout}
      emailSharingEnabled={emailConsent.enabled}
      meId={meId}
    />
  ) : null;

  const card = (
    <div
      className={styles.groupCardRoot}
      data-testid="group-card"
      data-layout={layout}
    >
      {layout === "stacked" && title != null && title !== "" && (
        // A heading, not `div`, so this rail section joins the page's h1/h2
        // outline (GroupCard is only ever rendered within `features/home`).
        <Text kind="label" as="h2" color="indigo500" bold>
          {title}
        </Text>
      )}
      {layout === "stacked" ? (
        <>
          <div className={styles.groupCard.stacked}>{content}</div>
          {members && (
            <>
              <Divider />
              {members}
              <Divider />
            </>
          )}
          {actions}
        </>
      ) : (
        <>
          <div className={styles.groupCard.wide}>
            {content}
            {actions}
          </div>
          {members && (
            <>
              <Divider />
              {members}
            </>
          )}
        </>
      )}
    </div>
  );

  // `stacked` (the rail) drops the timeline chrome entirely. The icon-in-a-
  // circle exists to mark this card's place in `Preparation`'s own vertical
  // timeline (its `verticalLine`, preparation.tsx). The rail has no such
  // sequence, since `RailSection` just stacks independent cards, so there is
  // nothing for the icon to mark. `wide` keeps it, with the same markup as
  // before this component took a `layout` prop.
  if (layout === "stacked") {
    return card;
  }

  return (
    <div className={homeStyles.steps}>
      <div className={homeStyles.stepIcon[isFinalised ? "final" : "initial"]}>
        <UserGroupIcon label="group people" size={30} />
      </div>
      {card}
    </div>
  );
}

interface ButtonComponentProps extends GroupCardProps {
  selfEnrolled: boolean;
  isFinalised: boolean;
}

function ButtonComponent(props: ButtonComponentProps) {
  const rootNavigate = useRootNavigate();
  const finaliseGroup = useFinaliseGroup(props.assessment.id);

  const {
    assessment,
    workGroupId,
    userGroup,
    changeRequested,
    selfEnrolled,
    isFinalised,
    layout = "wide",
  } = props;

  const openRequestModal = () => {
    if (workGroupId == null || workGroupId === "" || userGroup == null) return;
    show(GroupChangeRequestModal, {
      assessmentId: assessment.id,
      workGroupId,
      groupName: userGroup.name,
      existing: null,
    });
  };

  // The "Request Group Change" button only appears when the teacher allows it,
  // and is disabled once the cutoff has passed; the modal explains the rules.
  const groupChangeRequestsEnabled =
    props.assessment.settings?.groupChangeRequestsEnabled === true;
  const cutoff = props.assessment.settings?.groupChangeRequestCutoff;
  // Advisory only — the server rejects a late request with CUTOFF_PASSED; this
  // just disables the button early when the browser clock runs ahead.
  const isChangeCutoffPassed = cutoff != null && new Date(cutoff) < new Date();

  if (selfEnrolled) {
    // Deadline passed and no group — show disabled button
    if (isFinalised && !userGroup) {
      return (
        <Button
          kind="secondary"
          icon={<PlusIcon label="join a group" size={16} />}
          disabled
        >
          Join a Group
        </Button>
      );
    }

    if (!isFinalised) {
      const meetsMinLimit = Boolean(
        userGroup &&
        userGroup.minLimit != null &&
        getActiveMembers(userGroup).length >= userGroup.minLimit
      );

      if (userGroup && meetsMinLimit) {
        return (
          <div className={styles.groupEditButton}>
            <Button
              kind="outline"
              onClick={() => rootNavigate(`/group`)}
              icon={<PencilIcon label="change group" size={24} />}
            >
              Change Group
            </Button>
            <Button
              kind="secondary"
              onClick={() =>
                show(FinaliseGroupModal, {
                  onFinalise: () => finaliseGroup(),
                  deadline:
                    props.assessment.settings?.groupSelfEnrollmentDeadline,
                })
              }
              icon={<TickIcon label="finalise group" size={24} />}
            >
              Finalise Group
            </Button>
          </div>
        );
      }

      const Icon = userGroup ? (
        <UserGroupIcon label="join a group" size={24} />
      ) : (
        <PlusIcon label="join a group" size={16} />
      );
      return (
        <Button
          kind="secondary"
          icon={Icon}
          onClick={() => rootNavigate(`/group`)}
        >
          {userGroup ? "Edit Group" : "Join a Group"}
        </Button>
      );
    }
  }

  // A request already exists (in any status): `ChangeRequestSection` shows its
  // outcome instead, independent of whether new requests are currently allowed
  // (e.g. the cutoff has since passed). Withdrawing clears `changeRequested`
  // from the cache (see `use-group-change-request.tsx`), which brings the
  // "Request Group Change" button back.
  if (userGroup && groupChangeRequestsEnabled && !changeRequested) {
    return (
      <div className={styles.groupButton[layout]}>
        <Button
          kind="outline"
          className={styles.requestButton}
          // Home/stacked only: the prototype's `.btn.ghost.sm` is 32px tall.
          // `wide` (Preparation) keeps `size` unset so it still defaults to
          // "md" (40px), unchanged.
          size={layout === "stacked" ? "sm" : undefined}
          onClick={openRequestModal}
          disabled={isChangeCutoffPassed}
        >
          Request Group Change
        </Button>
      </div>
    );
  }

  return;
}

// Owns the "a request exists" half of the button row, kept out of
// `ButtonComponent` so neither the `openDate` gate nor the self-enrolment
// ladder can swallow it.
function ChangeRequestSection(props: GroupCardProps) {
  const {
    assessment,
    workGroupId,
    userGroup,
    changeRequested,
    layout = "wide",
  } = props;

  // Approval moves the student into a new group, which overwrites
  // `userGroup.name` once the query refetches — so the name has to be
  // captured while the request is still pending, before that happens.
  // Without a prior pending sighting (e.g. the card mounts straight into an
  // already-approved request), this stays `null` and the status block below
  // omits "Moved from" entirely.
  const [previousGroupName, setPreviousGroupName] = useState<string | null>(
    null
  );
  // Captured once per request id: the student can be moved by hand while the
  // request is still pending, and re-capturing on that rerender would leave
  // "Moved from" naming the destination group rather than the origin.
  const capturedFor = useRef<string | null>(null);
  useEffect(() => {
    if (changeRequested?.status !== GroupChangeRequestStatus.Pending) return;
    if (capturedFor.current === changeRequested.id) return;
    capturedFor.current = changeRequested.id;
    setPreviousGroupName(userGroup?.name ?? null);
  }, [changeRequested?.id, changeRequested?.status, userGroup?.name]);

  if (!userGroup || !changeRequested) return null;

  const openRequestModal = () => {
    if (workGroupId == null || workGroupId === "") return;
    show(GroupChangeRequestModal, {
      assessmentId: assessment.id,
      workGroupId,
      groupName: userGroup.name,
      existing: changeRequested,
    });
  };

  return (
    <div className={styles.groupButton[layout]}>
      <ChangeRequestStatusBlock
        request={changeRequested}
        previousGroupName={previousGroupName}
        onSeeMore={openRequestModal}
      />
    </div>
  );
}

// Renders the outcome of an existing group change request. Declined and
// expired share one visual (design spec: "same visual") since a student can't
// act differently on either — both just mean the request didn't go through.
function ChangeRequestStatusBlock(props: {
  request: MessageFragment;
  previousGroupName: string | null;
  onSeeMore: () => void;
}) {
  const { request, previousGroupName, onSeeMore } = props;
  const sentDate = formatDateShort(new Date(request.insertedAt));
  // Omit rather than fall back to `sentDate` when the request hasn't
  // actually been decided yet on the server — showing the sent date here
  // would misleadingly read as the decision date.
  const decidedDate =
    request.decidedAt != null
      ? formatDateShort(new Date(request.decidedAt))
      : null;

  if (request.status === GroupChangeRequestStatus.Pending) {
    return (
      <div className={styles.changeRequestStatus}>
        <Alert severity="info" size="sm">
          Change requested · your teacher has been notified
        </Alert>
        <Text kind="systemMd">Sent {sentDate}</Text>
        <LinkButton onClick={onSeeMore}>See more</LinkButton>
      </div>
    );
  }

  if (request.status === GroupChangeRequestStatus.Approved) {
    return (
      <div className={styles.changeRequestStatus}>
        <Alert severity="success" size="sm">
          Request approved · your group has been changed by your teacher
        </Alert>
        <Text kind="systemMd">
          {previousGroupName != null
            ? `Moved from ${previousGroupName}${decidedDate != null ? ` · ${decidedDate}` : ""}`
            : decidedDate != null
              ? `Approved ${decidedDate}`
              : ""}
        </Text>
      </div>
    );
  }

  // DECLINED | EXPIRED
  return (
    <div className={styles.changeRequestStatus}>
      <Alert severity="error" size="sm">
        Your request was not accepted
      </Alert>
      {request.decisionNote != null && request.decisionNote !== "" && (
        <>
          <Text kind="systemMd">
            Teacher&rsquo;s note{decidedDate != null ? ` · ${decidedDate}` : ""}
          </Text>
          <Text kind="bodySm" as="div" className={styles.decisionNoteQuote}>
            {request.decisionNote}
          </Text>
        </>
      )}
    </div>
  );
}

function Content(props: GroupCardProps) {
  const { userGroup, assessment, finalised } = props;
  const selfEnrolled = assessment.settings?.groupSelfEnrollmentEnabled;
  const deadline = assessment.settings?.groupSelfEnrollmentDeadline;
  const isDeadlinePassed =
    deadline != null ? new Date(deadline) < new Date() : false;

  let headerText: string;
  let title: string;

  if (!userGroup && selfEnrolled === true && (isDeadlinePassed || finalised)) {
    headerText = "You’ll be assigned to a group soon";
    title =
      "Self enrolment deadline has passed. Since you’ve not enrolled in any group yet, you’ll be assigned to a group by your teacher";
  } else if (selfEnrolled === true && userGroup) {
    headerText = `You have joined: ${userGroup.name}`;
    title = "You’ll collaborate with your group to submit a shared response.";
  } else if (selfEnrolled === true) {
    headerText = "Join a Group";
    title =
      "Join or create a group to participate in this project. If you do not select a group by the deadline, you will be automatically assigned to one.";
  } else {
    headerText = `You are a member of: ${userGroup?.name}`;
    title = userGroup ? "You were assigned to this group by your teacher." : "";
  }

  return (
    <div className={styles.groupCardContent}>
      <Text kind="headingFour">{headerText}</Text>
      <Text kind="bodyLine">{title}</Text>
    </div>
  );
}

interface GroupMembersProps {
  userGroup: GroupFragment;
  layout: "wide" | "stacked";
  /// Whether the teacher has turned email sharing on for this assessment at
  /// all. `stacked`-only: it explains a missing email in the options menu
  /// (`StackedMemberMenu`). `wide`'s `MemberItem` never renders a menu, so it
  /// has nothing to explain.
  emailSharingEnabled: boolean;
  /// The viewer's own user id. `stacked`-only: hides `StackedMemberMenu` on
  /// the viewer's own row. `wide`'s `MemberItem` never renders a menu, so it
  /// has nothing to gate.
  meId?: string;
}

// A member to display, flagged with their withdrawal status.
interface DisplayMember {
  user: UserFragment;
  withdrawn: boolean;
}

// Dispatches to the `wide` single-row layout (unit-fit, popover overflow) or
// the `stacked` rail's vertical scrolling list. Neither branch calls a hook
// itself: each one that needs to (`WideMemberRow`'s `useRowFit`) is its own
// component, so this can switch between them without violating the rules of
// hooks.
function GroupMembers(props: GroupMembersProps) {
  const { userGroup, layout, emailSharingEnabled, meId } = props;

  const activeMembers = getActiveMembers(userGroup);
  const withdrawnMembers = getWithdrawnMembers(userGroup);

  const displayList: DisplayMember[] = [
    ...activeMembers.map((user) => ({ user, withdrawn: false })),
    ...withdrawnMembers.map((user) => ({ user, withdrawn: true })),
  ];

  if (layout === "stacked") {
    return (
      <StackedMemberList
        displayList={displayList}
        emailSharingEnabled={emailSharingEnabled}
        meId={meId}
      />
    );
  }

  return (
    <WideMemberRow
      activeCount={activeMembers.length}
      displayList={displayList}
      groupName={userGroup.name}
    />
  );
}

interface WideMemberRowProps {
  activeCount: number;
  displayList: DisplayMember[];
  groupName: string;
}

// Unchanged from before this component took a `layout` prop: `stacked`
// never reaches this function.
function WideMemberRow(props: WideMemberRowProps) {
  const { activeCount, displayList, groupName } = props;

  // Any change that alters a rendered width and so the single-row fit: which
  // members show, their order, withdrawal state and names, plus the active
  // count in the label.
  const fitSignature = [
    activeCount,
    ...displayList.map(
      (m) => `${m.user.id}:${m.withdrawn ? 1 : 0}:${m.user.name}`
    ),
  ].join("|");

  const { containerRef, measureRef, visibleCount } = useRowFit(
    displayList.length,
    MEMBER_LIST_GAP,
    fitSignature
  );

  const displayMembers = displayList.slice(0, visibleCount);
  const remainingMembers = displayList.slice(visibleCount);

  return (
    <div ref={containerRef} className={styles.memberList}>
      <Text kind="label" as="div" className={styles.memberListLabel}>
        {activeCount} Members:
      </Text>
      {displayMembers.map((member) => (
        <MemberItem key={member.user.id} member={member} />
      ))}
      {remainingMembers.length > 0 && (
        <RemainingMembersPopover
          remainingMembers={remainingMembers}
          groupName={groupName}
        />
      )}
      {/* Off-screen row used only to measure intrinsic widths. The two sample
          pills must match the real overflow trigger element (a <button>, not a
          <span>) so their measured width matches — buttons don't inherit font.
          They give the pill's base and per-digit width (see useRowFit). */}
      <div ref={measureRef} className={styles.memberMeasureRow} aria-hidden>
        <Text kind="label" as="div" className={styles.memberListLabel}>
          {activeCount} Members:
        </Text>
        {displayList.map((member) => (
          <MemberItem key={member.user.id} member={member} />
        ))}
        <button className={styles.remainingButton}>+8</button>
        <button className={styles.remainingButton}>+88</button>
      </div>
    </div>
  );
}

// Scrolls rather than growing unbounded, since real groups run larger than
// the four-person example this matches. `RemainingMembersPopover` and
// `useRowFit` are `wide`-only: nothing here touches them.
function StackedMemberList(props: {
  displayList: DisplayMember[];
  emailSharingEnabled: boolean;
  meId?: string;
}) {
  return (
    <ScrollArea type="hover" className={styles.stackedMemberScroll}>
      <div className={styles.stackedMemberList}>
        {props.displayList.map((member) => (
          <StackedMemberRow
            key={member.user.id}
            member={member}
            emailSharingEnabled={props.emailSharingEnabled}
            meId={props.meId}
          />
        ))}
      </div>
    </ScrollArea>
  );
}

function StackedMemberRow(props: {
  member: DisplayMember;
  emailSharingEnabled: boolean;
  meId?: string;
}) {
  const { user, withdrawn } = props.member;

  // Email is only present when the teacher enabled "Show email IDs to
  // students" and both the viewer and this member consented to mutual
  // sharing. The backend returns null otherwise; see `MemberItem` below for
  // the same check.
  const hasEmail = user.email != null && user.email !== "";

  return (
    <div className={styles.stackedMemberRow}>
      <Avatar name={user.name} size="sm" shade={user.colourset ?? undefined} />
      <Text
        kind="bodySm"
        as="div"
        className={styles.stackedMemberName}
        style={withdrawn ? { color: colors.grey400 } : undefined}
      >
        {withdrawn ? `${user.name} (Withdrawn)` : user.name}
      </Text>
      {user.id !== props.meId && (
        <StackedMemberMenu
          name={user.name}
          email={hasEmail ? (user.email as string) : null}
          emailSharingEnabled={props.emailSharingEnabled}
        />
      )}
    </div>
  );
}

// The menu always renders. `hasEmail` above already gates whether an
// address exists; this component shouldn't also decide whether the student
// sees an explanation for its absence. Without a shared email, both actions
// render disabled via the design system's own disabled state, never
// simulated with opacity, and the label carries a reason instead of an
// address. That reason can only distinguish "teacher never enabled sharing"
// from "enabled but not mutually consented yet" because of the
// `emailSharingEnabled` flag threaded down from `useEmailConsent`; `email`
// alone is null in both cases.
function StackedMemberMenu(props: {
  name: string;
  email: string | null;
  emailSharingEnabled: boolean;
}) {
  const { name, email, emailSharingEnabled } = props;
  const hasEmail = email != null;

  const handleCopyEmail = () => {
    if (email == null) return;
    // copy-to-clipboard returns false rather than throwing when the
    // browser denies clipboard access, so a success toast can't be
    // unconditional: it would tell the student the address copied when
    // nothing happened.
    if (copy(email)) {
      toast.success(`Copied ${name}’s email address`);
    } else {
      toast.error(`Couldn’t copy ${name}’s email address`);
    }
  };

  const handleEmailThem = () => {
    if (email == null) return;
    // A raw address can carry reserved characters, `?` in particular,
    // which would otherwise reopen the mailto URL's own query string and
    // let the rest of the address be read back as header fields (`bcc=`
    // among them). Percent-encoding keeps it a single recipient token.
    window.open(`mailto:${encodeURIComponent(email)}`, "_blank");
  };

  const reason = emailSharingEnabled
    ? `Email hidden until you and ${name} both turn on sharing.`
    : "Your teacher hasn't turned on email sharing for this assessment.";

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <IconButton shape="square" aria-label={`${name} options`}>
          <EllipsisHorizontalIcon label="" size={20} />
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end">
        <DropdownMenu.Item
          content="Copy email address"
          onClick={handleCopyEmail}
          disabled={!hasEmail}
        />
        <DropdownMenu.Item
          content="Email them"
          onClick={handleEmailThem}
          disabled={!hasEmail}
        />
        <DropdownMenu.Label>
          <Text
            kind="bodySm"
            as="div"
            color="grey400"
            className={styles.stackedMemberEmail}
          >
            {hasEmail ? email : reason}
          </Text>
        </DropdownMenu.Label>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

// Toggle for the student's own mutual email sharing consent, shown when the
// teacher has enabled email sharing for the assessment.
function EmailConsentToggle(props: {
  emailConsent: ReturnType<typeof useEmailConsent>;
  layout: "wide" | "stacked";
}) {
  const { consent, setConsent } = props.emailConsent;

  // Names the switch's own description rather than leaving it as loose text
  // below, so the explanation reaches a screen reader on focus the way the
  // tooltip never did from a separate icon.
  const hintId = useId();

  return (
    <div className={styles.consentBlock[props.layout]}>
      <div className={styles.consentRow[props.layout]}>
        <Text kind="bodyMd">Share my email with group members</Text>
        <Switch
          checked={consent === true}
          onCheckedChange={(v) => setConsent(v)}
          aria-label="Share my email with group members"
          aria-describedby={hintId}
        />
      </div>
      <Text kind="bodySm" id={hintId} className={styles.consentHint}>
        {consent === true
          ? "Mutual sharing. You see emails only from members who also share."
          : "Emails hidden. Turn on to share yours and see members who share theirs."}
      </Text>
    </div>
  );
}

function MemberItem(props: { member: DisplayMember; truncate?: boolean }) {
  const { user, withdrawn } = props.member;
  const truncate = props.truncate ?? false;
  const item = (
    <div className={styles.memberItem}>
      <Avatar name={user.name} size="md" shade={user.colourset ?? undefined} />
      <Text
        kind="bodySm"
        as="div"
        className={truncate ? styles.memberName : styles.memberNameInline}
        style={withdrawn ? { color: colors.grey400 } : undefined}
      >
        {withdrawn ? `${user.name} (Withdrawn)` : user.name}
      </Text>
    </div>
  );

  // Email is only present when the teacher enabled "Show email IDs to students"
  // AND both the viewer and this member consented to mutual sharing (the
  // backend returns null otherwise), so the tooltip shows only then.
  if (user.email == null || user.email === "") return item;

  return (
    <Tooltip content={<Text kind="bodySm">{user.email}</Text>}>{item}</Tooltip>
  );
}

function RemainingMembersPopover(props: {
  groupName: string;
  remainingMembers: DisplayMember[];
}) {
  const { remainingMembers, groupName } = props;

  return (
    <Popover.Root modal={false}>
      <Popover.Trigger asChild>
        <button className={styles.remainingButton}>
          +{remainingMembers.length}
        </button>
      </Popover.Trigger>
      <Popover.Content>
        <Popover.Card width={500} style={{ height: 500 }}>
          <div className={styles.groupNameHeader}>
            <Text kind="headingSix">{groupName}</Text>
            <Text kind="bodySm">{remainingMembers.length} members</Text>
          </div>
          <Divider />
          <div className={styles.memberGrid}>
            {remainingMembers.map((member) => (
              <MemberItem key={member.user.id} member={member} truncate />
            ))}
          </div>
        </Popover.Card>
      </Popover.Content>
    </Popover.Root>
  );
}

const ContentDivider = styled(Divider)`
  margin: 10px 0;
  width: 60%;
`;
